import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useId } from 'react'
import { cn } from '@/lib/utils'
import { isExternalLink } from '@/lib/links'

export type FooterNavLink = {
    key: string
    href: string
    label: ReactNode
}

interface FooterLinkProps {
    href: string
    children: ReactNode
    className?: string
}

interface FooterNavProps {
    title: string
    links: Array<FooterNavLink>
    className?: string
}

export const FooterLink: FC<FooterLinkProps> = ({ href, children, className }): ReactNode => {
    const external = isExternalLink(href)

    return (
        <Link
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className={cn(
                'inline-flex rounded-sm text-[0.85rem] leading-[1.3] font-medium text-foreground transition-colors duration-300 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                className
            )}
        >
            {children}
        </Link>
    )
}

export const FooterNav: FC<FooterNavProps> = ({ title, links, className }): ReactNode => {
    const headingId = useId()

    return (
        <nav aria-labelledby={headingId} className={className}>
            <h2 id={headingId} className='mb-4 font-heading text-heading-5 font-medium opacity-70'>
                {title}
            </h2>

            <ul className='flex flex-col items-start gap-2.5'>
                {links.map(link => (
                    <li key={link.key}>
                        <FooterLink href={link.href}>
                            {link.label}
                        </FooterLink>
                    </li>
                ))}
            </ul>
        </nav>
    )
}
